import { useEffect, useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import back from '../../../src/assets/img/leadsform/backbtn2.svg';

// Theme switcher — ported from the claude.ai/design "growthos" project
// (App.jsx → ThemeSwitch + THEMES). Sets data-theme on <html> and persists
// the choice; the theme token overrides live in growthos-theme.css.
const GOS_THEMES = [
  { id: 'claude', name: 'Claude', side: '#eae6dc', accent: '#c96342' },
  { id: 'sapphire', name: 'Sapphire', side: '#1b2b4b', accent: '#2f5fd0' },
  { id: 'emerald', name: 'Emerald', side: '#21535E', accent: '#0e8c6f' },
  { id: 'ice', name: 'Ice Chrome', side: '#e7ebf2', accent: '#1a56db' },
  { id: 'solar', name: 'Solar Flare', side: '#f9e9df', accent: '#ff6b35', accent2: '#f7478a' },
  { id: 'mint', name: 'Neo Mint', side: '#e3f2ec', accent: '#059669', accent2: '#0891b2' },
  { id: 'superleap', name: 'Superleap', side: '#0a221d', accent: '#12b76a' },
  { id: 'forest', name: 'Rainforest', side: '#28511c', accent: '#6da0cd' },
  { id: 'everglade', name: 'Everglade', side: '#264233', accent: '#f16c6f' },
];

const GosThemeSwitch = () => {
  const [theme, setTheme] = useState(
    () =>
      document.documentElement.getAttribute('data-theme') ||
      localStorage.getItem('growthos-theme') ||
      'claude'
  );
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [open]);

  const apply = (t) => {
    try {
      localStorage.setItem('growthos-theme', t.id);
    } catch (e) {
      /* ignore */
    }
    setTheme(t.id);
    setOpen(false);
  };

  return (
    <div className="gos-theme-wrap">
      <button
        type="button"
        className={'gos-icon-btn' + (open ? ' bordered' : '')}
        title="Theme"
        aria-label="Theme"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 21a9 9 0 1 1 9-9c0 1.8-1.3 3-3 3h-2.2a2.3 2.3 0 0 0-1.7 3.9c.4.5.6 1 .6 1.4 0 .9-1 1.7-2.7.7ZM7.5 11h.01M10.5 7h.01M15 7.5h.01M17.5 11h.01" />
        </svg>
      </button>
      {open && (
        <div className="gos-theme-menu" onClick={(e) => e.stopPropagation()}>
          <div className="gos-theme-menu-label">Theme</div>
          {GOS_THEMES.map((t) => (
            <button key={t.id} type="button" className="gos-theme-opt" onClick={() => apply(t)}>
              <span className="gos-theme-swatch">
                <span className="gos-theme-dot" style={{ background: t.side }}></span>
                <span
                  className="gos-theme-dot"
                  style={{ background: t.accent2 ? `linear-gradient(135deg, ${t.accent}, ${t.accent2})` : t.accent }}
                ></span>
              </span>
              {t.name}
              {theme === t.id && (
                <span className="gos-theme-check">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const RightOffcanvas = ({ show, handleClose, handleLogout, user }) => {
  const currentApplicationVersion = process.env.REACT_APP_VERSION;

  return (
    <Offcanvas show={show} onHide={handleClose} placement="end">
      <Offcanvas.Body>
        <div className="logout-content">
          <div className="user-detail">
            <img src="/assets/images/user.png" alt="" />
          </div>
          <Offcanvas.Header closeButton></Offcanvas.Header>
        </div>
        <div className="logout-settings">
          <h5>Settings</h5>
          <img src="/assets/images/settings.png" alt="settings" />
        </div>
        <div className="setting-links"></div>
        <div className="version">UI Version: {currentApplicationVersion}</div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

const HeaderCRM = ({ handleToggleMenu, toggleMenu }) => {
  const isActive = (path) => pathname === path;
  const pathname = useLocation().pathname;
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const AttendanceFlag = process.env.REACT_APP_ATTENDANCE_FLAG === "true";
  const [navLinks, setNavLinks] = useState([
    'Lead',
    'Contacts',
    'Accounts',
    'Opportunity',
    'Tickets',
    'Meetings',
  ]);
  const [activeLink, setActiveLink] = useState(navLinks[0]);

  const [moreLinks, setMoreLinks] = useState([
    'Calls',
    'Campaigns',
    'Services',
    'Projects',
    'Reports',
    'Analytics',
  ]);

  const handleNavClick = (link, index) => {
    setActiveLink(link.toLowerCase());
    let arr = navLinks;
    const value = arr.pop();
    arr.unshift(link);
    setNavLinks(arr);
    moreLinks.splice(index, 1);
    const updatedMoreLinks = [...moreLinks, value];
    setMoreLinks(updatedMoreLinks);
  };

  const [isSticky, setIsSticky] = useState(false);
  const [smallPadding, setSmallPadding] = useState(false);

  const formatBreadcrumb = (breadcrumb) => {
    return breadcrumb
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Split camel case
      .toLowerCase() // Convert the entire string to lowercase
      .replace(/\b\w/g, (c) => c.toUpperCase()); // Capitalize each word
  };

  const pathSegments = pathname
    .split('/')
    .filter((segment) => segment) // Remove empty segments from leading/trailing slashes
    .map((segment) => formatBreadcrumb(segment)); // Format each segment

  console.log('test', pathSegments);

  // Generate full paths for linking
  const pathLinks = pathname
    .split('/')
    .filter((segment) => segment)
    .map((_, index, arr) => '/' + arr.slice(0, index + 1).join('/'));

  const nonLinkSegments = ['settings', 'products', 'sale'];

  const hasChild = pathSegments.length > 1;
  const navigate = useNavigate();
  const location = useLocation();
  const gotoAttendanceInfo = () => {
    navigate('/attendanceInfo');
  };
  const handleBack = () => {
    if (location.pathname === '/settings/templateGallery') {
      navigate('/settings/templates');
    } else if (location.pathname === '/leads/convertLead=Success') {
      navigate('/leads');
    } else if (location.pathname.includes('viewLead')) {
      navigate('/leads');
    } else if (location.pathname.includes('viewContact')) {
      navigate('/contacts');
    } else if (location.pathname.includes('viewAccount')) {
      navigate('/accounts');
    } else if (location.pathname.includes('viewOpportunity')) {
      navigate('/opportunity');
    } else if (location.pathname.includes('viewCampaign')) {
      navigate('/campaigns');
    } else {
      navigate(-1);
    }
  };
  const handleChangeDashboard = () => {
    if (location.pathname.includes('pnc')) {
      navigate('/dashboard');
    } else navigate('pncQuote/dashboard'); // e.g., "/dashboard" or "/products"
  };

  return (
    <>
      {pathname === '/leads/addLead/////////' ? (
        <div id="main_header">
          <div className="lead_hdbar">
            <div className="header-left">
              <div className="breadcrumb lft">
                <div>
                  <span title="Create Lead" className="curr">
                    Create Lead
                  </span>
                </div>
              </div>
            </div>
            <div className="header-right text-end">
              <div className="lead_end">
                <Link href="" className="cancel-lead ld-btn">
                  Cancel
                </Link>
                <Link href="" className="save-lead ld-btn">
                  Save
                </Link>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="dsk">
            <div id="main_header">
              <div className="header-left">
                {hasChild && pathname !== '/dashboard' && (
                  <div className="backButton" onClick={handleBack}>
                    <img src={back} alt="backlogo" width="14px" style={{ marginRight: '.5rem' }} />
                  </div>
                )}
                <div className="breadcrumb lft">
                  <div>
                    <Link to="/dashboard" title="Home">
                      Home
                    </Link>

                    {pathSegments.length > 0 && <span className="hs"> / </span>}

                    {pathSegments.map((segment, index) => {
                      const excluded = ['settings', 'products', 'sales'];
                      const isLast = index === pathSegments.length - 1;
                      const isExcluded = excluded.includes(segment.toLowerCase());

                      return (
                        <span key={index}>
                          {!isLast ? (
                            <>
                              {isExcluded ? (
                                <span style={{ color: "#707070" }}>{segment}</span> // plain text
                              ) : (
                                <Link to={pathLinks[index]}>{segment}</Link>
                              )}
                              <span className="hs"> / </span>
                            </>
                          ) : (
                            <span className="curr">{segment}</span> // current page
                          )}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="header-right text-end">
                <div className="button_align">
                  {/* Dashboard switcher moved to the dashboard page-head (see homeCRM.jsx) */}
                  {/* Attendance Info + Add(+) buttons commented out for now
                  {AttendanceFlag ? (
                    <div>
                      <button
                        type="button"
                        className="btn btn-danger w-100"
                        aria-expanded="false"
                        onClick={gotoAttendanceInfo}
                        style={{ lineHeight: 1.0 }}
                      >
                        Attendance Info
                      </button>
                    </div>
                  ) : null}

                  <div className="add_plus crmdropdown">
                    <div className="dropdown">
                      <button
                        className="plus_btn"
                        type="button"
                        id="dropdownMenuButton"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16px"
                          height="16px"
                          viewBox="0 0 22 22"
                        >
                          <g
                            id="Edit_Add_Plus"
                            data-name="Edit / Add_Plus"
                            transform="translate(5 5)"
                          >
                            <path
                              id="Vector"
                              d="M6,10.8h4.8m0,0h4.8m-4.8,0v4.8m0-4.8V6"
                              transform="translate(-5.047 -5.047)"
                              fill="none"
                              stroke="#fff"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="1"
                            />
                            <g
                              id="Ellipse_50"
                              data-name="Ellipse 50"
                              transform="translate(-5 -5)"
                              fill="none"
                              stroke="#fff"
                              strokeWidth="1"
                            >
                              <circle cx="11" cy="11" r="11" stroke="none" />
                              <circle cx="11" cy="11" r="10.5" fill="none" />
                            </g>
                          </g>
                        </svg>
                      </button>

                      <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton">
                        <li>
                          <Link className="dropdown-item" to={`/leads/CreateNew`}>
                            Add Lead
                          </Link>
                        </li>
                        <li>
                          <Link className="dropdown-item" to={`/contacts/addContact`}>
                            Add Contact
                          </Link>
                        </li>
                        <li>
                          <Link className="dropdown-item" to={`/accounts/addAccount`}>
                            Add Account
                          </Link>
                        </li>
                        <li>
                          <Link className="dropdown-item" to={`/opportunity/addOpportunity`}>
                            Add Opportunity
                          </Link>
                        </li>
                        <li>
                          <Link className="dropdown-item" to={`/campaigns/addCampaign`}>
                            Add Campaign
                          </Link>
                        </li>
                        <li>
                          <Link className="dropdown-item" to={`/tickets/addTicket`}>
                            Add Ticket
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                  */}

                  <div className="gos-tb-actions">
                    <GosThemeSwitch />
                    <button type="button" className="gos-icon-btn gos-dot-indicator" title="Notifications" aria-label="Notifications">
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9ZM13.7 21a2 2 0 0 1-3.4 0" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="gos-icon-btn"
                      title="Settings"
                      aria-label="Settings"
                      onClick={() => navigate('/settings')}
                    >
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 0 1-4 0v-.1A1.6 1.6 0 0 0 7 19.4a1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0-1.1-2.7H1a2 2 0 0 1 0-4h.1A1.6 1.6 0 0 0 2.6 7a1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H7a1.6 1.6 0 0 0 1-1.5V1a2 2 0 0 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V7a1.6 1.6 0 0 0 1.5 1H23a2 2 0 0 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1Z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mob">
            <div id="main_header-mob" style={{ marginLeft: toggleMenu ? '72px' : '0px' }}>
              <div className="main_hdr_top">
                <div className="toggleMenu" onClick={handleToggleMenu}>
                  <i className="bi bi-list"></i> <span>Menu</span>
                </div>

                <div className="add_plus crmdropdown">
                  <div className="dropdown">
                    <button
                      className="plus_btn"
                      type="button"
                      id="dropdownMenuButton"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16px"
                        height="16px"
                        viewBox="0 0 22 22"
                      >
                        <g
                          id="Edit_Add_Plus"
                          data-name="Edit / Add_Plus"
                          transform="translate(5 5)"
                        >
                          <path
                            id="Vector"
                            d="M6,10.8h4.8m0,0h4.8m-4.8,0v4.8m0-4.8V6"
                            transform="translate(-5.047 -5.047)"
                            fill="none"
                            stroke="#fff"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="1"
                          />
                          <g
                            id="Ellipse_50"
                            data-name="Ellipse 50"
                            transform="translate(-5 -5)"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="1"
                          >
                            <circle cx="11" cy="11" r="11" stroke="none" />
                            <circle cx="11" cy="11" r="10.5" fill="none" />
                          </g>
                        </g>
                      </svg>
                    </button>

                    <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton">
                      <li>
                        <Link className="dropdown-item" to={`/leads/CreateNew`}>
                          Add Lead
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item" to={`/contacts/addContact`}>
                          Add Contact
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item" to={`/accounts/addAccount`}>
                          Add Account
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item" to={`/opportunity/addOpportunity`}>
                          Add Opportunity
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item" to={`/campaigns/addCampaign`}>
                          Add Campaign
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item" to={`/tickets/addTicket`}>
                          Add Ticket
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="header-left">
                {hasChild && pathname !== '/dashboard' && (
                  <div className="backButton" onClick={handleBack}>
                    <img src={back} alt="backlogo" width="14px" style={{ marginRight: '.5rem' }} />
                  </div>
                )}
                <div className="breadcrumb lft">
                  <div>
                    <Link to="/dashboard" title="Home">
                      Home
                    </Link>{' '}
                    {pathname === '/dashboard' ? (
                      <></>
                    ) : (
                      <span title="Client" className="curr">
                        {pathSegments}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default HeaderCRM;
